import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Container,
  Card,
  Row as BootstrapRow,
  Col as BootstrapCol,
  Button,
  Alert,
  Nav,
} from 'react-bootstrap';
import { Table, Select, Modal, Form, Button as AntdButton, Badge as AntdBadge, App } from 'antd';
import { FilterOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import FormItem from '@/common/FormItem/FormItem';
import type { ColumnType, TableRowSelection } from 'antd/es/table/interface';
import PageTitle from '@/common/PageTitle/PageTitle';
import LifecycleRulesDrawer, {
  LifecycleRulesInfoButton,
} from '@/common/LifecycleRulesDrawer/LifecycleRulesDrawer';
import { SONG_LIFECYCLE_RULES } from '@/features/lifecycle-rules/lifecycleRulesContent';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  useListTracksQuery,
  useCountTracksQuery,
  useUpdateTrackGenreMutation,
  useArchiveTrackMutation,
  useRestoreTrackMutation,
  usePreflightTrackChannelImpactMutation,
  usePreflightPermanentDeleteTrackMutation,
  usePermanentDeleteTrackMutation,
  useCreateTrackPermanentDeleteBulkOpMutation,
  useLazyGetTrackPermanentDeleteBulkOpQuery,
  extractTracksErrorMessage,
  isBenignTrackLifecycleConflict,
  scheduleTracksListReconcile,
  scheduleSongHistoryInvalidateAfterSongLifecycle,
  scheduleSongLibraryDeleteRemovalGuard,
  patchTracksCountsForDelete,
  patchTracksListsRemove,
  PermanentDeletePreflight,
  TrackChannelImpact,
  Track,
  TrackStatus,
  ListTracksArgs,
} from '@/features/tracks/tracksApi';
import {
  TRACK_PD_BULK_TERMINAL,
  bulkDeleteFailuresFromBulkOp,
  clearTrackPdBulkOpPointer,
  completedCountFromBulkOp,
  isAmbiguousBulkOpCreateError,
  newTrackPdBulkIdempotencyKey,
  readTrackPdBulkOpPointer,
  resolveSucceededTrackIdsForCachePatch,
  sleepMs,
  writeTrackPdBulkOpPointer,
} from '@/features/tracks/trackPermanentDeleteBulkOp';
import {
  aggregatePermanentDeletePreflights,
  BULK_PD_PREFLIGHT_CONCURRENCY,
  BULK_PD_PREFLIGHT_MAX_IDS,
  mapWithConcurrency,
} from '@/features/tracks/permanentDeletePreflight';
import {
  buildTrackLifecycleBulkSummary,
  parseTrackLifecycleBulkFailures,
  failuresFromPermanentDeleteRow,
  type TrackLifecycleBulkAction,
} from '@/features/tracks/trackLifecycleBulkResults';
import {
  BulkOperationResultsModal,
  type BulkOperationResultsSummary,
} from '@/features/bulk-operation-results';
import {
  TRACKS_RECONCILE_DELAY_MS,
  resolveLifecycleTabCount,
} from '@/features/tracks/lifecycleCountPatch';
import { useBatchActions, BatchActionValue } from '@/features/tracks/useBatchActions';
import { LinkedJobCount } from '@/features/tracks/LinkedJobCount';
import {
  clearTrackIdFormFilter,
  stripTrackIdSearchParam,
} from '@/features/tracks/songLibraryDeepLink';
import { useListArtistsQuery } from '@/features/artists/artistsApi';
import { useListRecordLabelsQuery } from '@/features/record-labels/recordLabelsApi';
import { useListAlbumsQuery } from '@/features/albums/albumsApi';
import { useListGenresQuery } from '@/features/genres/genresApi';
import {
  applyGenreFilterToTrackListArgs,
  buildTrackGenrePatch,
  trackGenrePatchIsEmpty,
} from '@/features/tracks/trackGenreEdit';
import { useReplaceSongAudioMutation, extractProcessingErrorMessage, extractReplacementAlreadyInProgress, scheduleProcessingJobsInvalidateAfterSongDelete } from '@/features/processing/processingApi';
import {
  ReplaceSongAudioConfirmModal,
  type ReplaceSongAudioTarget,
} from '@/features/processing/ReplaceSongAudioConfirmModal';
import { initAndPutReplaceSongAudio } from '@/features/processing/replaceSongAudioClient';
import { validateMp3File } from '@/features/processing/processingUploadService';
import {
  useCanPerformLifecycleDelete,
  useCanEditPage,
} from '@/contexts/SuperAdminIdentityContext';
import { getMusicApprovedCdnBaseUrl } from '@/lib/adminApi';
import { formatDateTime } from '@/features/processing/processingDisplay';
import { useUploaderDirectory } from '@/features/processing/uploaderDisplay';
import { EntityRefBadge } from '@/components/EntityRef/EntityRefBadge';
import { buildEntityRefs, formatEntityRef, normalizeRefSearchTerm } from '@/lib/entityRef';
import { SONG_LIBRARY_ACTION_ICONS } from '@/styles/songLibraryActionIcons';
import { SchedulePlacementStack } from '@/features/channels/scheduleSideDisplay';
import rowActions from '@/styles/rowActions.module.scss';
import styles from './songs.module.scss';

// Song Library — Track catalog with MDL Active / Archived / All lifecycle views.
// Tracks are created only by the music processing pipeline. Soft archive /
// restore use catalog lifecycle APIs. Permanent Delete from Archived uses the
// coordinated lineage-deletion workflow (MDL-3).

const TABLE_PAGE_SIZE = 50;
const DELETE_CONFIRM_TOKEN = 'DELETE';
const UPLOADER_PAGE_ID = 'uploader';
const SONGS_PAGE_ID = 'songs';

type LifecycleView = TrackStatus | 'all';

type ChannelImpactChannel = NonNullable<TrackChannelImpact['channels']>[number];

function uniquePlacementRows(channel: ChannelImpactChannel): Array<{
  key: string;
  scheduleSide: string | null;
  rotationLabel: string;
}> {
  const rows: Array<{
    key: string;
    scheduleSide: string | null;
    rotationLabel: string;
  }> = [];
  const seen = new Set<string>();
  for (const rot of channel.rotations || []) {
    const rotationLabel =
      rot.rotationName || rot.category || rot.rotationId || null;
    if (!rotationLabel) continue;
    const scheduleSide = rot.scheduleSide || null;
    const key = `${rot.rotationId || rotationLabel}::${scheduleSide || ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({
      key,
      scheduleSide,
      rotationLabel: String(rotationLabel),
    });
  }
  // Fallback when rotations are missing but channel-level schedule sides exist.
  if (rows.length === 0 && (channel.scheduleSides || []).length > 0) {
    for (const side of channel.scheduleSides || []) {
      rows.push({
        key: `side-${side}`,
        scheduleSide: side,
        rotationLabel: '—',
      });
    }
  }
  return rows;
}

function ChannelImpactChannelList(props: {
  channels: ChannelImpactChannel[];
}) {
  const { channels } = props;
  if (!channels.length) return null;
  return (
    <ul style={{ marginBottom: 0, marginTop: 8, paddingLeft: 18 }}>
      {channels.map((ch) => {
        const name = ch.channelName || ch.channelId || 'Unknown channel';
        const placements = uniquePlacementRows(ch);
        return (
          <li
            key={ch.channelId || ch.channelName || name}
            style={{ marginBottom: 10 }}
          >
            {placements.length > 0 ? (
              placements.map((row) => (
                <div key={`${name}-${row.key}`} style={{ marginBottom: 8 }}>
                  <SchedulePlacementStack
                    scheduleSide={row.scheduleSide}
                    channel={name}
                    rotation={row.rotationLabel}
                    compact
                  />
                </div>
              ))
            ) : (
              <SchedulePlacementStack
                scheduleSide={ch.scheduleSides?.[0] || null}
                channel={
                  ch.placementCount
                    ? `${name} — ${ch.placementCount} placement${
                        ch.placementCount === 1 ? '' : 's'
                      }`
                    : name
                }
                rotation={null}
                compact
              />
            )}
          </li>
        );
      })}
    </ul>
  );
}
type SongRow = {
  trackId: string;
  title: string;
  artistName: string;
  albumTitle: string;
  recordLabelName: string;
  releaseYear: number | null;
  isrc: string;
  songType: string;
  kkExclusive: 'YES' | 'NO';
  playable: boolean;
  status: TrackStatus;
  playbackVersionNumber: number | null;
  playbackArtifactVersionId: string | null;
  approvedObjectKey: string | null;
  sourceProcessingJobId: string | null;
  /** Genre taxonomy (no table column in v1 — filters + edit modal only). */
  genreId: string | null;
  subgenreId: string | null;
  archivedAt: string | null;
  archivedBy: string | null;
  archiveReason: string | null;
  createdAt: string;
};

const UNRESOLVED = '—';

function parseLifecycleView(raw: string | null): LifecycleView {
  if (raw === 'archived' || raw === 'all' || raw === 'active') return raw;
  return 'active';
}

function formatTabCount(n: number | undefined): string {
  if (n == null || n < 0) return '';
  return ` (${n.toLocaleString()})`;
}

function toSongRow(
  track: Track,
  artistNames: Map<string, string>,
  labelNames: Map<string, string>,
  albumTitles: Map<string, string>,
): SongRow {
  return {
    trackId: track.trackId,
    // Untitled tracks fall back to the human-facing TR ref, never a raw UUID.
    title: track.title || formatEntityRef('track', track.trackId),
    artistName:
      (track.primaryArtistId && artistNames.get(track.primaryArtistId)) || UNRESOLVED,
    albumTitle: (track.albumId && albumTitles.get(track.albumId)) || UNRESOLVED,
    recordLabelName:
      (track.recordLabelId && labelNames.get(track.recordLabelId)) || UNRESOLVED,
    releaseYear: typeof track.releaseYear === 'number' ? track.releaseYear : null,
    isrc: track.isrc || UNRESOLVED,
    songType: track.songType || UNRESOLVED,
    kkExclusive: track.songType === 'kk-original' ? 'YES' : 'NO',
    playable: track.playable === true,
    status: track.status === 'archived' ? 'archived' : 'active',
    playbackVersionNumber: track.playbackVersionNumber ?? null,
    playbackArtifactVersionId: track.playbackArtifactVersionId || null,
    approvedObjectKey: track.approvedObjectKey || null,
    sourceProcessingJobId: track.sourceProcessingJobId || null,
    genreId: track.genreId ?? null,
    subgenreId: track.subgenreId ?? null,
    archivedAt: track.archivedAt || track.deletedAt || null,
    archivedBy: track.archivedBy || track.deletedBySub || null,
    archiveReason: track.archiveReason || null,
    createdAt: track.createdAt,
  };
}

const Songs: React.FC = () => {
  const { message } = App.useApp();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canLifecycle = useCanPerformLifecycleDelete();
  const canReplaceAudio = useCanEditPage(UPLOADER_PAGE_ID);
  const canEditSongs = useCanEditPage(SONGS_PAGE_ID);
  const cdnBaseUrl = getMusicApprovedCdnBaseUrl();
  const uploaderDirectory = useUploaderDirectory();
  const [tablePage, setTablePage] = useState(1);
  const [replaceSongAudio] = useReplaceSongAudioMutation();
  const replaceFileInputRef = useRef<HTMLInputElement | null>(null);
  const [replacePickRow, setReplacePickRow] = useState<SongRow | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<ReplaceSongAudioTarget | null>(null);
  const [replaceError, setReplaceError] = useState('');
  const [replaceErrorOpenPath, setReplaceErrorOpenPath] = useState<string | null>(null);
  const [replaceBusy, setReplaceBusy] = useState(false);

  const scheduleReconcile = useCallback(
    (op?: 'archive' | 'restore' | 'delete', trackIds?: string[]) => {
      scheduleTracksListReconcile(dispatch, {
        delayMs: TRACKS_RECONCILE_DELAY_MS,
        op,
        trackIds,
      });
    },
    [dispatch],
  );

  const initialView = parseLifecycleView(searchParams.get('status'));
  const deepLinkTrackId = searchParams.get('trackId') || null;
  const catalogAlbumId = searchParams.get('albumId') || null;
  const catalogArtistId = searchParams.get('artistId') || null;
  const catalogRecordLabelId = searchParams.get('recordLabelId') || null;

  const [view, setView] = useState<LifecycleView>(initialView);
  const [highlightTrackId, setHighlightTrackId] = useState<string | null>(deepLinkTrackId);

  // Genre catalog v1 — server-side Genre/Subgenre list filter (no table column).
  const [genreFilterId, setGenreFilterId] = useState<string | undefined>(undefined);
  const [subgenreFilterId, setSubgenreFilterId] = useState<string | undefined>(
    undefined,
  );

  useEffect(() => {
    const next = parseLifecycleView(searchParams.get('status'));
    setView(next);
    setHighlightTrackId(searchParams.get('trackId'));
  }, [searchParams]);

  const switchView = (next: LifecycleView) => {
    setView(next);
    // Tab switch invalidates both the selection and any latched action value
    // (the options differ per tab).
    resetBatchUi();
    setHighlightTrackId(null);
    setFilters((prev) => clearTrackIdFormFilter(prev));
    setTablePage(1);
    setResetCounter((prev) => prev + 1);
    const params = stripTrackIdSearchParam(searchParams);
    if (next === 'active') params.delete('status');
    else params.set('status', next);
    setSearchParams(params, { replace: true });
  };

  /** Drop deep-link Track ID from URL, highlight, form filter, and page state. */
  const clearDeepLinkTrackFilter = useCallback(() => {
    setHighlightTrackId(null);
    setFilters((prev) => clearTrackIdFormFilter(prev));
    setTablePage(1);
    setResetCounter((prev) => prev + 1);
    setSearchParams(stripTrackIdSearchParam(searchParams), { replace: true });
  }, [searchParams, setSearchParams]);

  const listTracksArgs = useMemo((): ListTracksArgs => {
    const status: ListTracksArgs['status'] = view === 'all' ? 'all' : view;
    if (highlightTrackId) {
      return { status, trackId: highlightTrackId, limit: 200 };
    }
    const base: ListTracksArgs = { status, limit: 200 };
    if (catalogAlbumId) base.albumId = catalogAlbumId;
    else if (catalogArtistId) base.artistId = catalogArtistId;
    else if (catalogRecordLabelId) base.recordLabelId = catalogRecordLabelId;
    return applyGenreFilterToTrackListArgs(base, genreFilterId, subgenreFilterId);
  }, [
    view,
    highlightTrackId,
    catalogAlbumId,
    catalogArtistId,
    catalogRecordLabelId,
    genreFilterId,
    subgenreFilterId,
  ]);

  // Warm Active + Archived list caches (same scopes as the page, never the
  // deep-link single-track filter) so tab labels track membership after
  // Archive/Restore even while the operator stays on the other tab.
  const warmListScopeArgs = useMemo((): Omit<ListTracksArgs, 'status'> => {
    const base: Omit<ListTracksArgs, 'status'> = { limit: 200 };
    if (catalogAlbumId) base.albumId = catalogAlbumId;
    else if (catalogArtistId) base.artistId = catalogArtistId;
    else if (catalogRecordLabelId) base.recordLabelId = catalogRecordLabelId;
    // Genre filter is part of the page scope — warm caches stay aligned.
    return applyGenreFilterToTrackListArgs(base, genreFilterId, subgenreFilterId);
  }, [
    catalogAlbumId,
    catalogArtistId,
    catalogRecordLabelId,
    genreFilterId,
    subgenreFilterId,
  ]);

  const songLibraryListQueryOpts = {
    // Keep optimistic Archive/Restore membership when remounting tabs —
    // a lagging projection refetch can otherwise wipe sibling rows / counts.
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  } as const;

  // When deep-linking a single track, list with trackId; otherwise full view
  // page (optionally scoped by album/artist/recordLabel from catalog pages).
  const {
    data: tracksData,
    isLoading: tracksLoading,
    isFetching: tracksFetching,
    error: tracksError,
    refetch: refetchTracks,
  } = useListTracksQuery(listTracksArgs, songLibraryListQueryOpts);

  const activeListWarmQuery = useListTracksQuery(
    { ...warmListScopeArgs, status: 'active' },
    songLibraryListQueryOpts,
  );
  const archivedListWarmQuery = useListTracksQuery(
    { ...warmListScopeArgs, status: 'archived' },
    songLibraryListQueryOpts,
  );

  // Same refetch policy as listTracks — unguarded count refetch was overwriting
  // optimistic Active/Archived totals while the table still showed the patched list.
  const activeCountQuery = useCountTracksQuery(
    { status: 'active' },
    songLibraryListQueryOpts,
  );
  const archivedCountQuery = useCountTracksQuery(
    { status: 'archived' },
    songLibraryListQueryOpts,
  );

  const { data: artistsData } = useListArtistsQuery({ status: 'all', limit: 200 });
  const { data: labelsData } = useListRecordLabelsQuery({ status: 'all', limit: 200 });
  const { data: albumsData } = useListAlbumsQuery({ status: 'all', limit: 200 });
  // Genre taxonomy — status=all so archived assignments can still resolve a
  // name for the "(Archived)" label; pickers/filters offer active-only.
  const { data: genresData } = useListGenresQuery({ status: 'all', counts: false });

  const [archiveTrack] = useArchiveTrackMutation();
  const [restoreTrack] = useRestoreTrackMutation();
  const [updateTrackGenre] = useUpdateTrackGenreMutation();
  const [preflightChannelImpact] = usePreflightTrackChannelImpactMutation();
  const [preflightPermanentDelete] = usePreflightPermanentDeleteTrackMutation();
  const [permanentDeleteTrack] = usePermanentDeleteTrackMutation();
  const [createTrackPermanentDeleteBulkOp] =
    useCreateTrackPermanentDeleteBulkOpMutation();
  const [fetchTrackPermanentDeleteBulkOp] =
    useLazyGetTrackPermanentDeleteBulkOpQuery();

  const artistNames = useMemo(() => {
    const map = new Map<string, string>();
    (artistsData?.artists || []).forEach((a) => map.set(a.artistId, a.name));
    return map;
  }, [artistsData]);

  const labelNames = useMemo(() => {
    const map = new Map<string, string>();
    (labelsData?.recordLabels || []).forEach((l) => map.set(l.recordLabelId, l.name));
    return map;
  }, [labelsData]);

  const albumTitles = useMemo(() => {
    const map = new Map<string, string>();
    (albumsData?.albums || []).forEach((a) => map.set(a.albumId, a.title));
    return map;
  }, [albumsData]);

  // ---- Genre taxonomy (filters + edit modal only in v1 — no table column) ---
  const allGenres = useMemo(() => genresData?.genres ?? [], [genresData]);
  const activeGenres = useMemo(
    () => allGenres.filter((g) => g.status === 'active'),
    [allGenres],
  );
  const genreById = useMemo(
    () => new Map(allGenres.map((g) => [g.genreId, g])),
    [allGenres],
  );
  const subgenreById = useMemo(() => {
    const map = new Map<
      string,
      { subgenreId: string; genreId: string; name: string; status: string }
    >();
    for (const genre of allGenres) {
      for (const sub of genre.subgenres ?? []) {
        map.set(sub.subgenreId, sub);
      }
    }
    return map;
  }, [allGenres]);

  const [genreEditTarget, setGenreEditTarget] = useState<SongRow | null>(null);
  const [genreEditGenreId, setGenreEditGenreId] = useState<string | null>(null);
  const [genreEditSubgenreId, setGenreEditSubgenreId] = useState<string | null>(
    null,
  );
  const [genreEditSaving, setGenreEditSaving] = useState(false);

  const openGenreEdit = (row: SongRow) => {
    if (!canEditSongs) return;
    setGenreEditTarget(row);
    setGenreEditGenreId(row.genreId);
    setGenreEditSubgenreId(row.subgenreId);
  };

  const closeGenreEdit = () => {
    if (genreEditSaving) return;
    setGenreEditTarget(null);
    setGenreEditGenreId(null);
    setGenreEditSubgenreId(null);
  };

  /**
   * Genre select options: active taxonomy, plus the Track's current archived
   * genre labeled "(Archived)" so the operator may keep it without a forced
   * change. Archived taxonomy can be KEPT — never newly assigned.
   */
  const genreEditGenreOptions = useMemo(() => {
    const options = activeGenres.map((g) => ({ label: g.name, value: g.genreId }));
    const currentId = genreEditTarget?.genreId;
    if (currentId && !activeGenres.some((g) => g.genreId === currentId)) {
      const current = genreById.get(currentId);
      options.unshift({
        label: `${current?.name || currentId} (Archived)`,
        value: currentId,
      });
    }
    return options;
  }, [activeGenres, genreById, genreEditTarget]);

  const genreEditSubgenreOptions = useMemo(() => {
    if (!genreEditGenreId) return [];
    const genre = genreById.get(genreEditGenreId);
    const activeSubs = (genre?.subgenres ?? []).filter(
      (s) => s.status === 'active',
    );
    const options = activeSubs.map((s) => ({ label: s.name, value: s.subgenreId }));
    // Current archived subgenre stays selectable as "(Archived)" when the
    // Track still points at it under the currently selected genre.
    const currentSubId = genreEditTarget?.subgenreId;
    if (
      currentSubId &&
      genreEditTarget?.genreId === genreEditGenreId &&
      !activeSubs.some((s) => s.subgenreId === currentSubId)
    ) {
      const currentSub = subgenreById.get(currentSubId);
      options.unshift({
        label: `${currentSub?.name || currentSubId} (Archived)`,
        value: currentSubId,
      });
    }
    return options;
  }, [genreById, genreEditGenreId, genreEditTarget, subgenreById]);

  const submitGenreEdit = async () => {
    if (!genreEditTarget) return;
    const patch = buildTrackGenrePatch(
      { genreId: genreEditTarget.genreId, subgenreId: genreEditTarget.subgenreId },
      { genreId: genreEditGenreId, subgenreId: genreEditSubgenreId },
    );
    if (trackGenrePatchIsEmpty(patch)) {
      closeGenreEdit();
      return;
    }
    setGenreEditSaving(true);
    try {
      await updateTrackGenre({
        trackId: genreEditTarget.trackId,
        ...patch,
      }).unwrap();
      message.success('Song genre updated');
      setGenreEditSaving(false);
      setGenreEditTarget(null);
      setGenreEditGenreId(null);
      setGenreEditSubgenreId(null);
    } catch (err) {
      setGenreEditSaving(false);
      message.error(extractTracksErrorMessage(err) || 'Failed to update song genre');
    }
  };

  const songs: SongRow[] = useMemo(
    () =>
      (tracksData?.tracks || []).map((t) =>
        toSongRow(t, artistNames, labelNames, albumTitles),
      ),
    [tracksData, artistNames, labelNames, albumTitles],
  );

  // ---- Playback ------------------------------------------------------------
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      // Detach the source WITHOUT `src = ''`: an empty src attribute resolves
      // to the page URL, which the media element tries (and fails) to load,
      // firing the `error` listener — that surfaced a spurious "Playback
      // failed" toast on every Pause (owner QA 2026-07-21). removeAttribute +
      // load() aborts the download cleanly with no error event.
      audioRef.current.removeAttribute('src');
      audioRef.current.load();
    }
    setPlayingTrackId(null);
  }, []);

  const togglePlayback = useCallback(
    (row: SongRow) => {
      if (!cdnBaseUrl || !row.approvedObjectKey || !row.playable) return;
      if (playingTrackId === row.trackId) {
        stopPlayback();
        return;
      }
      if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.addEventListener('ended', () => setPlayingTrackId(null));
        audioRef.current.addEventListener('error', () => {
          // Intentional teardown (stopPlayback detaches src) must not surface
          // an error toast — only real load/decode failures should.
          if (!audioRef.current?.getAttribute('src')) return;
          setPlayingTrackId(null);
          message.error('Playback failed for this track');
        });
      }
      audioRef.current.src = `${cdnBaseUrl}/${row.approvedObjectKey}`;
      void audioRef.current.play().catch(() => {
        setPlayingTrackId(null);
        message.error('Playback failed for this track');
      });
      setPlayingTrackId(row.trackId);
    },
    [cdnBaseUrl, playingTrackId, stopPlayback, message],
  );

  useEffect(() => () => stopPlayback(), [stopPlayback]);

  // ---- Archive / Restore ---------------------------------------------------
  const [archiveLoading, setArchiveLoading] = useState<string | null>(null);
  const [restoreLoading, setRestoreLoading] = useState<string | null>(null);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [trackToArchive, setTrackToArchive] = useState<{ trackId: string; title: string } | null>(null);
  const [archiveImpact, setArchiveImpact] = useState<TrackChannelImpact | null>(null);
  const [archiveImpactLoading, setArchiveImpactLoading] = useState(false);
  const [archiveImpactError, setArchiveImpactError] = useState<string | null>(null);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [trackToRestore, setTrackToRestore] = useState<{ trackId: string; title: string } | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    trackId: string;
    title: string;
  } | null>(null);
  const [deletePreflight, setDeletePreflight] =
    useState<PermanentDeletePreflight | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteConfirmTyped, setDeleteConfirmTyped] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deletePhase, setDeletePhase] = useState<
    'idle' | 'preflight' | 'confirm' | 'running' | 'done' | 'error'
  >('idle');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [bulkDeleteIds, setBulkDeleteIds] = useState<string[]>([]);
  const [pdBulkProgress, setPdBulkProgress] = useState<{
    completed: number;
    total: number;
  } | null>(null);
  const pdBulkPollGenRef = useRef(0);
  /** Bumped on open/close so Cancel discards in-flight preflight results. */
  const deletePreflightGenerationRef = useRef(0);
  /** Session-scoped last Archive/Restore/PD operation result (not a durable queue). */
  const [lastBulkResult, setLastBulkResult] =
    useState<BulkOperationResultsSummary | null>(null);
  const [bulkResultsOpen, setBulkResultsOpen] = useState(false);
  const [bulkRetryBusy, setBulkRetryBusy] = useState(false);
  const [batchConfirmBusy, setBatchConfirmBusy] = useState(false);
  const [lifecycleHelpOpen, setLifecycleHelpOpen] = useState(false);

  // Batch Actions state lives in a shared hook. The action value is
  // `BatchActionValue | null` — NEVER undefined — because antd treats a
  // controlled Select with value={undefined} as uncontrolled and latches the
  // last selection internally (the "stuck on Delete Selected" bug).
  const {
    selectedRowKeys,
    setSelectedRowKeys,
    batchAction,
    batchModalOpen,
    chooseBatchAction,
    resetBatchUi,
    clearBatchAction,
  } = useBatchActions();

  const openBulkResults = (
    summary: BulkOperationResultsSummary,
    opts?: { forceOpen?: boolean },
  ) => {
    setLastBulkResult(summary);
    if (opts?.forceOpen || summary.failed > 0) {
      setBulkResultsOpen(true);
    }
  };

  /** Single-song Archive / Permanent Delete failure → same Operation Results modal as bulk. */
  const presentSingleLifecycleFailure = (
    action: TrackLifecycleBulkAction,
    trackId: string,
    err: unknown,
    identity?: { title?: string | null; artistName?: string | null } | null,
  ) => {
    const failures = parseTrackLifecycleBulkFailures(
      trackId,
      err,
      identity || songIdentityByTrackId.get(trackId) || null,
    );
    const summary = buildTrackLifecycleBulkSummary({
      action,
      requested: 1,
      succeeded: 0,
      skipped: 0,
      failures,
    });
    openBulkResults(summary, { forceOpen: true });
    message.warning('Operation failed. View results for details.', 6);
  };

  const dismissLastBulkResult = () => {
    setBulkResultsOpen(false);
    setLastBulkResult(null);
  };

  const songIdentityByTrackId = useMemo(() => {
    const map = new Map<string, { title: string; artistName: string }>();
    for (const row of songs) {
      if (row.trackId) {
        map.set(row.trackId, {
          title: row.title || row.trackId,
          artistName: row.artistName || '—',
        });
      }
    }
    return map;
  }, [songs]);

  // Every path out of the MDL delete modal funnels through here so no stale
  // preflight/token/reason/phase can leak into the next operation.
  // Durable BulkOp progress may continue server-side; sessionStorage pointer remains.
  const closeDeleteModal = () => {
    deletePreflightGenerationRef.current += 1;
    setDeleteModalOpen(false);
    setDeleteTarget(null);
    setBulkDeleteIds([]);
    setDeletePreflight(null);
    setDeleteReason('');
    setDeleteConfirmTyped('');
    setDeleteError(null);
    setDeletePhase('idle');
    setDeleteLoading(false);
    setPdBulkProgress(null);
  };

  const finalizeTrackPdBulkOp = useCallback(
    (
      finalOp: {
        bulkOpId: string;
        status: string;
        totalMatched: number;
        counters?: {
          succeeded?: number;
          failed?: number;
          skipped?: number;
          retryable?: number;
        };
        results?: Array<{
          trackId: string;
          title?: string | null;
          status: string;
          ok?: boolean;
          skipped?: boolean;
          retryable?: boolean;
          message?: string | null;
          reasonCode?: string | null;
        }>;
        errorMessage?: string | null;
      },
      submittedIds?: string[] | null,
    ) => {
      const pointer = readTrackPdBulkOpPointer();
      const fallbackIds =
        (submittedIds && submittedIds.length > 0
          ? submittedIds
          : pointer?.trackIds) || [];
      clearTrackPdBulkOpPointer();
      setPdBulkProgress(null);
      setDeleteLoading(false);

      if (finalOp.status === 'failed') {
        setDeleteError(
          finalOp.errorMessage || 'Permanent delete operation failed',
        );
        setDeletePhase('error');
        setDeleteModalOpen(true);
        message.error(finalOp.errorMessage || 'Permanent delete operation failed');
        return;
      }

      const okIds = resolveSucceededTrackIdsForCachePatch(finalOp, fallbackIds);
      const pdFailures = bulkDeleteFailuresFromBulkOp(finalOp).flatMap((row) =>
        failuresFromPermanentDeleteRow(
          row,
          songIdentityByTrackId.get(row.trackId) || {
            title: row.title ?? null,
            artistName: null,
          },
        ),
      );
      const succeeded = finalOp.counters?.succeeded ?? okIds.length;
      const failed = finalOp.counters?.failed ?? 0;
      const skipped = finalOp.counters?.skipped ?? 0;
      const requested = finalOp.totalMatched || okIds.length + failed + skipped;

      if (okIds.length > 0) {
        // A) Immediate deterministic patch from succeeded Track IDs.
        patchTracksListsRemove(dispatch, okIds);
        patchTracksCountsForDelete(dispatch, okIds.length);
        // Guard against lagging LIST refetch resurrecting deleted rows.
        scheduleSongLibraryDeleteRemovalGuard(dispatch, okIds);
        // B) Delayed count/list ladder (projection catch-up).
        scheduleReconcile('delete', okIds);
        scheduleProcessingJobsInvalidateAfterSongDelete(dispatch, okIds);
        scheduleSongHistoryInvalidateAfterSongLifecycle(dispatch, {
          trackIds: okIds,
          op: 'delete',
        });
      }

      closeDeleteModal();
      resetBatchUi();

      const pdSummary = buildTrackLifecycleBulkSummary({
        action: 'permanentDelete',
        requested,
        succeeded,
        skipped,
        failures: pdFailures,
      });
      openBulkResults(pdSummary, { forceOpen: failed > 0 });
      if (failed > 0) {
        message.warning(
          `Permanently deleted ${succeeded}; ${failed} failed. View results for details.`,
          8,
        );
      } else {
        message.success(
          `Permanently deleted ${succeeded} song(s)` +
            (skipped ? `; ${skipped} skipped` : ''),
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openBulkResults/resetBatchUi/closeDeleteModal are stable enough for this page
    [dispatch, message, scheduleReconcile, songIdentityByTrackId],
  );

  const pollTrackPdBulkOpUntilDone = useCallback(
    async (bulkOpId: string, totalMatched: number) => {
      const gen = ++pdBulkPollGenRef.current;
      setPdBulkProgress({ completed: 0, total: totalMatched });
      for (let i = 0; i < 450; i += 1) {
        if (pdBulkPollGenRef.current !== gen) return null;
        const op = await fetchTrackPermanentDeleteBulkOp(bulkOpId).unwrap();
        if (pdBulkPollGenRef.current !== gen) return null;
        setPdBulkProgress({
          completed: completedCountFromBulkOp(op),
          total: op.totalMatched || totalMatched,
        });
        if (TRACK_PD_BULK_TERMINAL.has(op.status)) return op;
        await sleepMs(2000);
      }
      throw new Error(
        'Permanent delete is still running — return to Song Library to resume progress',
      );
    },
    [fetchTrackPermanentDeleteBulkOp],
  );

  // Recover durable BulkOp pointer after refresh/navigation (server owns the work).
  useEffect(() => {
    if (!canLifecycle) return;
    const pointer = readTrackPdBulkOpPointer();
    if (!pointer?.bulkOpId) return;
    let cancelled = false;
    void (async () => {
      try {
        setDeleteLoading(true);
        setDeletePhase('running');
        setDeleteModalOpen(true);
        setDeleteTarget({
          trackId: pointer.bulkOpId,
          title: `${pointer.totalMatched} selected songs`,
        });
        setBulkDeleteIds(pointer.trackIds || []);
        const finalOp = await pollTrackPdBulkOpUntilDone(
          pointer.bulkOpId,
          pointer.totalMatched,
        );
        if (cancelled || !finalOp) return;
        finalizeTrackPdBulkOp(finalOp, pointer.trackIds || []);
      } catch (err) {
        if (cancelled) return;
        setDeleteError(
          extractTracksErrorMessage(err) ||
            (err instanceof Error ? err.message : 'Permanent delete failed'),
        );
        setDeletePhase('error');
        setDeleteModalOpen(true);
      } finally {
        if (!cancelled) setDeleteLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // Mount / auth gate only — poll helpers are stable enough for recovery.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canLifecycle]);

  const openPermanentDelete = async (trackId: string, title: string) => {
    if (!canLifecycle) return;
    const generation = deletePreflightGenerationRef.current + 1;
    deletePreflightGenerationRef.current = generation;
    setDeleteTarget({ trackId, title });
    setDeletePreflight(null);
    setDeleteReason('');
    setDeleteConfirmTyped('');
    setDeleteError(null);
    setBulkDeleteIds([]);
    setDeletePhase('preflight');
    setDeleteModalOpen(true);
    try {
      const pre = await preflightPermanentDelete({ trackId }).unwrap();
      if (deletePreflightGenerationRef.current !== generation) return;
      setDeletePreflight(pre);
      setDeletePhase('confirm');
    } catch (err) {
      if (deletePreflightGenerationRef.current !== generation) return;
      setDeleteError(
        extractTracksErrorMessage(err) || 'Could not load deletion preflight',
      );
      setDeletePhase('error');
    }
  };

  const openBulkPermanentDelete = async (ids: string[]) => {
    if (!canLifecycle || ids.length === 0) return;
    const generation = deletePreflightGenerationRef.current + 1;
    deletePreflightGenerationRef.current = generation;
    setBulkDeleteIds(ids);
    setDeleteTarget({
      trackId: ids[0],
      title: `${ids.length} selected songs`,
    });
    setDeletePreflight(null);
    setDeleteReason('');
    setDeleteConfirmTyped('');
    setDeleteError(null);
    setDeletePhase('preflight');
    setDeleteModalOpen(true);
    try {
      // Same dry-run preflight contract as single-song — bounded concurrency
      // only shortens wall-clock wait (was strictly serial N+1).
      const boundedIds = ids.slice(0, BULK_PD_PREFLIGHT_MAX_IDS);
      const startedAt =
        typeof performance !== 'undefined' ? performance.now() : Date.now();
      const preflights = await mapWithConcurrency(
        boundedIds,
        BULK_PD_PREFLIGHT_CONCURRENCY,
        async (id) => preflightPermanentDelete({ trackId: id }).unwrap(),
      );
      if (deletePreflightGenerationRef.current !== generation) return;
      const elapsedMs =
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
        startedAt;
      if (typeof console !== 'undefined' && console.info) {
        console.info(
          `[Song Library] bulk PD preflight: ${boundedIds.length} songs in ${Math.round(elapsedMs)}ms (concurrency=${BULK_PD_PREFLIGHT_CONCURRENCY})`,
        );
      }
      setDeletePreflight(
        aggregatePermanentDeletePreflights({
          ids: boundedIds,
          preflights,
        }),
      );
      setDeletePhase('confirm');
    } catch (err) {
      if (deletePreflightGenerationRef.current !== generation) return;
      setDeleteError(
        extractTracksErrorMessage(err) || 'Could not load bulk deletion preflight',
      );
      setDeletePhase('error');
    }
  };

  const handleConfirmPermanentDelete = async () => {
    if (!canLifecycle || !deleteTarget) return;
    if (deleteConfirmTyped !== DELETE_CONFIRM_TOKEN) {
      message.error(`Type ${DELETE_CONFIRM_TOKEN} to confirm`);
      return;
    }
    if (!deleteReason.trim()) {
      message.error('Deletion reason is required');
      return;
    }
    setDeleteLoading(true);
    setDeletePhase('running');
    setDeleteError(null);
    try {
      if (bulkDeleteIds.length > 0) {
        const ids = [...bulkDeleteIds];
        const reason = deleteReason.trim();
        const idempotencyKey = newTrackPdBulkIdempotencyKey();
        const createArgs = {
          ids,
          reason,
          idempotencyKey,
          selectionMode: 'ids' as const,
        };

        let created;
        try {
          created = await createTrackPermanentDeleteBulkOp(createArgs).unwrap();
        } catch (err) {
          if (!isAmbiguousBulkOpCreateError(err)) throw err;
          // Ambiguous create (timeout/5xx): reconcile via same idempotency key.
          created = await createTrackPermanentDeleteBulkOp(createArgs).unwrap();
        }

        writeTrackPdBulkOpPointer({
          bulkOpId: created.bulkOpId,
          totalMatched: created.totalMatched || ids.length,
          idempotencyKey,
          startedAt: new Date().toISOString(),
          trackIds: ids,
        });

        const finalOp = await pollTrackPdBulkOpUntilDone(
          created.bulkOpId,
          created.totalMatched || ids.length,
        );
        if (!finalOp) return;
        finalizeTrackPdBulkOp(finalOp, ids);
      } else {
        await permanentDeleteTrack({
          trackId: deleteTarget.trackId,
          confirmTitle: deletePreflight?.title || deleteTarget.title,
          confirmTrackId: deleteTarget.trackId,
          reason: deleteReason.trim(),
        }).unwrap();
        scheduleReconcile('delete', [deleteTarget.trackId]);
        scheduleProcessingJobsInvalidateAfterSongDelete(dispatch, [
          deleteTarget.trackId,
        ]);
        message.success('Song permanently deleted — lineage removed; tombstones retained');
        closeDeleteModal();
      }
    } catch (err) {
      // Continuity / other hard failures: close confirm UI and open Operation Results
      // (same shell as bulk Permanent Delete — never toast-only).
      if (bulkDeleteIds.length === 0 && deleteTarget) {
        const trackId = deleteTarget.trackId;
        const identity =
          songIdentityByTrackId.get(trackId) || {
            title: deleteTarget.title,
            artistName: null,
          };
        closeDeleteModal();
        presentSingleLifecycleFailure('permanentDelete', trackId, err, identity);
      } else {
        setDeleteError(
          extractTracksErrorMessage(err) || 'Permanent delete failed',
        );
        setDeletePhase('error');
        message.error(
          extractTracksErrorMessage(err) || 'Permanent delete failed',
        );
      }
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleArchiveTrack = async (trackId: string, title: string) => {
    if (!canLifecycle) return;
    setTrackToArchive({ trackId, title });
    setArchiveImpact(null);
    setArchiveImpactError(null);
    setArchiveImpactLoading(true);
    setArchiveModalOpen(true);
    try {
      const impact = await preflightChannelImpact({ trackId }).unwrap();
      setArchiveImpact(impact);
    } catch (err) {
      setArchiveImpactError(
        extractTracksErrorMessage(err) ||
          'Could not load channel programming impact',
      );
    } finally {
      setArchiveImpactLoading(false);
    }
  };

  const handleConfirmArchive = async () => {
    if (!trackToArchive) return;
    const { trackId, title } = trackToArchive;
    if (archiveImpact?.blocked) {
      message.error(
        archiveImpact.blockers?.[0]?.message ||
          'Archive blocked: would leave a channel with no playable Songs',
      );
      return;
    }
    try {
      setArchiveLoading(trackId);
      if (playingTrackId === trackId) stopPlayback();
      await archiveTrack({ trackId }).unwrap();
      message.success('Song archived — moved to Archived Song Library');
      setArchiveModalOpen(false);
      setTrackToArchive(null);
      setArchiveImpact(null);
      setArchiveImpactError(null);
      scheduleReconcile('archive', [trackId]);
    } catch (err) {
      setArchiveModalOpen(false);
      setTrackToArchive(null);
      presentSingleLifecycleFailure(
        'archive',
        trackId,
        err,
        songIdentityByTrackId.get(trackId) || { title, artistName: null },
      );
    } finally {
      setArchiveLoading(null);
    }
  };

  const handleConfirmRestore = async () => {
    if (!trackToRestore) return;
    try {
      setRestoreLoading(trackToRestore.trackId);
      const restoredId = trackToRestore.trackId;
      await restoreTrack({ trackId: restoredId }).unwrap();
      message.success('Song restored — moved to Active Song Library');
      setRestoreModalOpen(false);
      setTrackToRestore(null);
      scheduleReconcile('restore', [restoredId]);
    } catch (err) {
      message.error(extractTracksErrorMessage(err) || 'Failed to restore song');
    } finally {
      setRestoreLoading(null);
    }
  };

  // ---- Filters -------------------------------------------------------------
  const [filters, setFilters] = useState<{
    songNames?: string;
    artists?: string;
    recordLabels?: string;
    isrcs?: string;
    kkExclusive?: string;
    playableFilter?: string;
    trackIdFilter?: string;
  }>({});
  const [resetCounter, setResetCounter] = useState(0);

  useEffect(() => {
    if (highlightTrackId) {
      setFilters((prev) => ({ ...prev, trackIdFilter: highlightTrackId }));
    }
  }, [highlightTrackId]);

  const filterOptions = useMemo(() => {
    const uniq = (values: string[]) =>
      Array.from(new Set(values.filter((v) => v && v !== UNRESOLVED))).sort();
    return {
      songNames: uniq(songs.map((s) => s.title)),
      artists: uniq(songs.map((s) => s.artistName)),
      recordLabels: uniq(songs.map((s) => s.recordLabelName)),
      isrcs: uniq(songs.map((s) => s.isrc)),
      kkExclusive: ['YES', 'NO'],
      playable: ['YES', 'NO'],
    };
  }, [songs]);

  const handleFilterChange = (key: string, value: string | undefined) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    setFilters({});
    setGenreFilterId(undefined);
    setSubgenreFilterId(undefined);
    clearBatchAction();
    setHighlightTrackId(null);
    setTablePage(1);
    setResetCounter((prev) => prev + 1);
    setSearchParams(stripTrackIdSearchParam(searchParams), { replace: true });
  };

  // Collision-safe human-facing TR refs for the loaded page (platform
  // standard, owner 2026-07-21). Canonical track_ ids stay in state, APIs,
  // and tooltips.
  const trackRefs = useMemo(
    () => buildEntityRefs('track', songs.map((s) => s.trackId)),
    [songs],
  );

  const filteredSongs = songs.filter((song) => {
    return (
      (!filters.songNames || song.title === filters.songNames) &&
      (!filters.artists || song.artistName === filters.artists) &&
      (!filters.recordLabels || song.recordLabelName === filters.recordLabels) &&
      (!filters.isrcs || song.isrc === filters.isrcs) &&
      (!filters.kkExclusive || song.kkExclusive === filters.kkExclusive) &&
      (!filters.playableFilter ||
        (filters.playableFilter === 'YES') === song.playable) &&
      (!filters.trackIdFilter ||
        song.trackId
          .toLowerCase()
          .includes(
            normalizeRefSearchTerm('track', filters.trackIdFilter).toLowerCase(),
          ))
    );
  });

  // Empty-page step-back after optimistic removals (client table pagination).
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredSongs.length / TABLE_PAGE_SIZE) || 1);
    if (tablePage > maxPage) setTablePage(maxPage);
  }, [filteredSongs.length, tablePage]);

  const selectedRows = useMemo(
    () => filteredSongs.filter((s) => selectedRowKeys.includes(s.trackId)),
    [filteredSongs, selectedRowKeys],
  );

  const selectionLifecycle = useMemo(() => {
    if (selectedRows.length === 0) return 'none' as const;
    const statuses = new Set(selectedRows.map((r) => r.status));
    if (statuses.size > 1) return 'mixed' as const;
    return selectedRows[0].status;
  }, [selectedRows]);

  // Status-scoped subsets of the current selection (mixed All-tab selections
  // run each batch action against its matching subset only).
  const selectedActiveIds = useMemo(
    () =>
      selectedRows.filter((r) => r.status === 'active').map((r) => r.trackId),
    [selectedRows],
  );
  const selectedArchivedIds = useMemo(
    () =>
      selectedRows
        .filter((r) => r.status === 'archived')
        .map((r) => r.trackId),
    [selectedRows],
  );

  const batchOptions = useMemo(() => {
    if (view === 'active') {
      return [{ label: 'Archive Selected', value: 'archive' }];
    }
    if (view === 'archived') {
      return [
        { label: 'Restore Selected', value: 'restore' },
        { label: 'Delete Selected', value: 'delete' },
      ];
    }
    if (selectionLifecycle === 'active') {
      return [{ label: 'Archive Selected', value: 'archive' }];
    }
    if (selectionLifecycle === 'archived') {
      return [
        { label: 'Restore Selected', value: 'restore' },
        { label: 'Delete Selected', value: 'delete' },
      ];
    }
    if (selectionLifecycle === 'mixed') {
      // Owner-approved 2026-08-11: a mixed selection on the All tab offers
      // status-scoped actions instead of disabling Batch Actions.
      return [
        {
          label: `Archive Active (${selectedActiveIds.length})`,
          value: 'archive',
        },
        {
          label: `Restore Archived (${selectedArchivedIds.length})`,
          value: 'restore',
        },
        {
          label: `Delete Archived (${selectedArchivedIds.length})`,
          value: 'delete',
        },
      ];
    }
    return [];
  }, [
    view,
    selectionLifecycle,
    selectedActiveIds.length,
    selectedArchivedIds.length,
  ]);

  /** Ids the latched batch action will run against (scoped when mixed). */
  const batchTargetIds = useMemo(() => {
    if (selectionLifecycle === 'mixed') {
      if (batchAction === 'archive') return selectedActiveIds;
      if (batchAction === 'restore') return selectedArchivedIds;
      return [];
    }
    return Array.from(new Set(selectedRowKeys.map((key) => String(key))));
  }, [selectionLifecycle, batchAction, selectedActiveIds, selectedArchivedIds, selectedRowKeys]);

  const handleBatchAction = (value: BatchActionValue) => {
    chooseBatchAction(value, selectionLifecycle, {
      onMixedSelection: () => {
        message.warning(
          'Select only Active songs to Archive, or only Archived songs to Restore/Delete.',
        );
      },
      // Permanent delete has exactly one confirmation experience: the MDL
      // preflight/confirm modal. Skip the generic batch pre-confirm entirely.
      onDelete: (ids) => void openBulkPermanentDelete(ids),
      mixedScope: {
        activeIds: selectedActiveIds,
        archivedIds: selectedArchivedIds,
      },
    });
  };

  const runBulkArchiveOrRestore = async (
    action: 'archive' | 'restore',
    trackIds: string[],
  ): Promise<BulkOperationResultsSummary> => {
    const uniqueKeys = Array.from(new Set(trackIds.map((id) => String(id))));
    const failures: ReturnType<typeof parseTrackLifecycleBulkFailures>[number][] = [];
    const succeededIds: string[] = [];
    let succeeded = 0;
    let skipped = 0;
    for (const trackId of uniqueKeys) {
      try {
        // eslint-disable-next-line no-await-in-loop
        if (action === 'archive') {
          await archiveTrack({ trackId }).unwrap();
        } else {
          await restoreTrack({ trackId }).unwrap();
        }
        succeeded += 1;
        succeededIds.push(trackId);
      } catch (err) {
        if (isBenignTrackLifecycleConflict(action, err)) {
          skipped += 1;
          continue;
        }
        failures.push(
          ...parseTrackLifecycleBulkFailures(
            trackId,
            err,
            songIdentityByTrackId.get(trackId) || null,
          ),
        );
      }
    }
    if (succeededIds.length > 0 || skipped > 0) {
      scheduleReconcile(
        action === 'restore' ? 'restore' : 'archive',
        succeededIds,
      );
    }
    return buildTrackLifecycleBulkSummary({
      action,
      requested: uniqueKeys.length,
      succeeded,
      skipped,
      failures,
    });
  };

  const handleConfirmBatch = async () => {
    if (!canLifecycle || !batchAction || batchConfirmBusy) return;
    if (batchAction !== 'archive' && batchAction !== 'restore') return;

    // Mixed selections run against the status-scoped subset only (Archive →
    // Active rows, Restore → Archived rows); single-status selections keep
    // the full selection as before.
    const uniqueKeys = Array.from(new Set(batchTargetIds));
    if (uniqueKeys.length === 0) return;
    setBatchConfirmBusy(true);
    try {
      const summary = await runBulkArchiveOrRestore(batchAction, uniqueKeys);
      openBulkResults(summary);
      const verb = summary.actionLabel;
      if (summary.failed === 0 && (summary.succeeded > 0 || summary.skipped > 0)) {
        message.success(
          `${verb} ${summary.succeeded} song(s)` +
            (summary.skipped ? `; ${summary.skipped} skipped` : ''),
        );
      } else if (summary.succeeded > 0 && summary.failed > 0) {
        message.warning(
          `${verb} ${summary.succeeded} song(s); ${summary.failed} failed. View results for details.`,
          8,
        );
      } else if (summary.failed > 0) {
        message.error(
          `Failed to ${batchAction} ${summary.failed} song(s). View results for details.`,
          8,
        );
      }
      resetBatchUi();
    } finally {
      setBatchConfirmBusy(false);
    }
  };

  const handleRetryBulkRetryable = async (trackIds: string[]) => {
    if (!lastBulkResult || trackIds.length === 0) return;
    const action = lastBulkResult.operationKey as TrackLifecycleBulkAction | undefined;
    setBulkRetryBusy(true);
    try {
      if (action === 'archive' || action === 'restore') {
        const summary = await runBulkArchiveOrRestore(action, trackIds);
        openBulkResults(summary);
        message.info(
          `Retry finished: ${summary.succeeded} succeeded, ${summary.failed} failed.`,
          6,
        );
        return;
      }
      // Permanent delete retry: reopen confirm flow with the retryable ids.
      setBulkResultsOpen(false);
      void openBulkPermanentDelete(trackIds);
      message.info(
        'Confirm Permanent Delete again for the retryable Songs (reason + DELETE required).',
        8,
      );
    } finally {
      setBulkRetryBusy(false);
    }
  };

  // Selection framework stays visible on every lifecycle tab (owner UX).
  const rowSelection: TableRowSelection<SongRow> | undefined = canLifecycle
    ? {
        selectedRowKeys,
        onChange: (keys) => setSelectedRowKeys(keys),
      }
    : undefined;

  const viewJob = (row: SongRow) => {
    if (row.sourceProcessingJobId) {
      navigate(`/processing-jobs/${encodeURIComponent(row.sourceProcessingJobId)}`);
      return;
    }
    message.info('No source Song Processing job on this Track yet.');
  };

  const openReplaceFilePicker = (row: SongRow) => {
    setReplaceError('');
    setReplaceErrorOpenPath(null);
    setReplacePickRow(row);
    const input = replaceFileInputRef.current;
    if (input) {
      input.value = '';
      input.click();
    }
  };

  const onReplaceFilePicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    const row = replacePickRow;
    setReplacePickRow(null);
    if (!file || !row) return;
    const validationError = validateMp3File(file);
    if (validationError) {
      message.error(validationError);
      return;
    }
    setReplaceTarget({
      trackId: row.trackId,
      title: row.title,
      primaryArtistName:
        row.artistName && row.artistName !== UNRESOLVED ? row.artistName : null,
      songType:
        row.songType === 'kk-original' || row.songType === 'commercial'
          ? row.songType
          : null,
      sourceProcessingJobId: row.sourceProcessingJobId,
      playbackVersionNumber: row.playbackVersionNumber,
      approvedObjectKey: row.approvedObjectKey,
      checksumApproved: null,
      file,
    });
  };

  const submitReplaceSongAudio = async (replaceReason: string) => {
    if (!replaceTarget) return;
    setReplaceBusy(true);
    setReplaceError('');
    setReplaceErrorOpenPath(null);
    try {
      const init = await initAndPutReplaceSongAudio({
        replaceSongAudio,
        trackId: replaceTarget.trackId,
        file: replaceTarget.file,
        replaceReason,
        songType: replaceTarget.songType,
      });
      message.success('Replace Song Audio started — processing the new file');
      setReplaceTarget(null);
      navigate(`/processing-jobs/${encodeURIComponent(init.jobId)}`);
    } catch (err) {
      const conflict = extractReplacementAlreadyInProgress(err);
      if (conflict) {
        setReplaceError(conflict.message);
        setReplaceErrorOpenPath(conflict.openReplacementPath);
      } else {
        setReplaceError(
          extractProcessingErrorMessage(err) || 'Replace Song Audio failed',
        );
        setReplaceErrorOpenPath(null);
      }
    } finally {
      setReplaceBusy(false);
    }
  };

  // ---- Columns -------------------------------------------------------------
  // KrossKast format: Artist then Title.
  const columns: ColumnType<SongRow>[] = [
    {
      title: 'Artist',
      dataIndex: 'artistName',
      key: 'artistName',
      sorter: (a, b) => a.artistName.localeCompare(b.artistName),
      render: (value: string) =>
        value !== UNRESOLVED ? (
          <Link to={`/artists?name=${encodeURIComponent(value)}`}>{value}</Link>
        ) : (
          value
        ),
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: 'Album',
      dataIndex: 'albumTitle',
      key: 'albumTitle',
      sorter: (a, b) => a.albumTitle.localeCompare(b.albumTitle),
      render: (value: string) =>
        value !== UNRESOLVED ? (
          <Link to={`/albums?title=${encodeURIComponent(value)}`}>{value}</Link>
        ) : (
          value
        ),
    },
    {
      title: 'Record Label',
      dataIndex: 'recordLabelName',
      key: 'recordLabelName',
      sorter: (a, b) => a.recordLabelName.localeCompare(b.recordLabelName),
    },
    {
      title: 'Track ID',
      dataIndex: 'trackId',
      key: 'trackId',
      width: 130,
      render: (id: string) => (
        <EntityRefBadge kind="track" id={id} refLabel={trackRefs.get(id)} />
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: TrackStatus) =>
        status === 'archived' ? (
          <AntdBadge status="default" text="Archived" />
        ) : (
          <AntdBadge status="success" text="Active" />
        ),
    },
    ...(view === 'archived' || view === 'all'
      ? ([
          {
            title: 'Archived',
            key: 'archivedInfo',
            width: 200,
            render: (_: unknown, row: SongRow) =>
              !row.archivedAt ? (
                <span className={styles.muted}>{UNRESOLVED}</span>
              ) : (
                <div>
                  <div>{formatDateTime(row.archivedAt ?? undefined)}</div>
                  {row.archivedBy ? (
                    <div style={{ fontSize: 12 }}>
                      {uploaderDirectory.resolve(row.archivedBy).label}
                    </div>
                  ) : null}
                  {row.archiveReason ? (
                    <div style={{ fontSize: 12, color: '#888' }}>{row.archiveReason}</div>
                  ) : null}
                </div>
              ),
          },
        ] as ColumnType<SongRow>[])
      : []),
    {
      title: 'Jobs',
      key: 'jobCount',
      width: 70,
      render: (_: unknown, row: SongRow) => <LinkedJobCount trackId={row.trackId} />,
    },
    {
      title: 'AAV',
      key: 'aav',
      width: 90,
      render: (_: unknown, row: SongRow) =>
        row.playbackVersionNumber != null ? `v${row.playbackVersionNumber}` : UNRESOLVED,
    },
    {
      title: 'Playable',
      dataIndex: 'playable',
      key: 'playable',
      width: 90,
      render: (_: unknown, record: SongRow) =>
        record.playable ? (
          <AntdBadge status="success" text="Yes" />
        ) : (
          <AntdBadge status="default" text="No" />
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, record: SongRow) => (
        <span className={rowActions.rowActions}>
          {view === 'active' &&
          record.status === 'active' &&
          cdnBaseUrl &&
          record.playable &&
          record.approvedObjectKey ? (
            <button
              type="button"
              className={rowActions.rowActionBtn}
              aria-label={playingTrackId === record.trackId ? 'Pause' : 'Play'}
              title={playingTrackId === record.trackId ? 'Pause' : 'Play'}
              onClick={() => togglePlayback(record)}
            >
              <i
                className={
                  playingTrackId === record.trackId
                    ? SONG_LIBRARY_ACTION_ICONS.pause
                    : SONG_LIBRARY_ACTION_ICONS.play
                }
                aria-hidden="true"
              />
            </button>
          ) : null}

          <button
            type="button"
            className={rowActions.rowActionBtn}
            aria-label="View Song Processing"
            title="View Song Processing"
            onClick={() => viewJob(record)}
          >
            <i className={SONG_LIBRARY_ACTION_ICONS.viewProcessing} aria-hidden="true" />
          </button>

          {record.status === 'active' && canEditSongs ? (
            <button
              type="button"
              className={rowActions.rowActionBtn}
              aria-label="Edit Genre"
              title="Edit Genre / Subgenre"
              data-testid="song-library-edit-genre"
              onClick={() => openGenreEdit(record)}
            >
              <i className={SONG_LIBRARY_ACTION_ICONS.editGenre} aria-hidden="true" />
            </button>
          ) : null}

          {view === 'active' && record.status === 'active' && canReplaceAudio ? (
            <button
              type="button"
              className={rowActions.rowActionBtn}
              aria-label="Replace Song Audio"
              title="Replace Song Audio — upload new audio for this Song"
              data-testid="song-library-replace-audio"
              onClick={() => openReplaceFilePicker(record)}
            >
              <i className={SONG_LIBRARY_ACTION_ICONS.replaceAudio} aria-hidden="true" />
            </button>
          ) : null}

          {view === 'active' && record.status === 'active' && canLifecycle ? (
            <button
              type="button"
              className={rowActions.rowActionBtn}
              aria-label="Archive"
              title="Archive"
              disabled={archiveLoading === record.trackId}
              onClick={() => handleArchiveTrack(record.trackId, record.title)}
            >
              <i className={SONG_LIBRARY_ACTION_ICONS.archive} aria-hidden="true" />
            </button>
          ) : null}

          {(view === 'archived' || view === 'all') &&
          record.status === 'archived' &&
          canLifecycle ? (
            <>
              <button
                type="button"
                className={rowActions.rowActionBtn}
                aria-label="Restore"
                title="Restore"
                disabled={restoreLoading === record.trackId}
                onClick={() => {
                  setTrackToRestore({ trackId: record.trackId, title: record.title });
                  setRestoreModalOpen(true);
                }}
              >
                <i className={SONG_LIBRARY_ACTION_ICONS.restore} aria-hidden="true" />
              </button>
              <button
                type="button"
                className={`${rowActions.rowActionBtn} ${rowActions.rowActionBtnDanger}`}
                aria-label="Delete permanently"
                title="Delete permanently"
                onClick={() => void openPermanentDelete(record.trackId, record.title)}
              >
                <i className={SONG_LIBRARY_ACTION_ICONS.permanentDelete} aria-hidden="true" />
              </button>
            </>
          ) : null}
        </span>
      ),
    },
  ];

  const loadErrorMessage = tracksError
    ? extractTracksErrorMessage(tracksError) || 'Failed to load the Song Library'
    : null;

  // Prefer a complete warm list page over lagging countTracks; never show a
  // tab total below an incomplete loaded page for that lifecycle.
  const activeCount = resolveLifecycleTabCount({
    serverCount: activeCountQuery.data?.count,
    loadedCount: activeListWarmQuery.data?.tracks?.length ?? 0,
    hasListData: activeListWarmQuery.data != null,
    listComplete:
      activeListWarmQuery.data != null && !activeListWarmQuery.data.pageToken,
  });
  const archivedCount = resolveLifecycleTabCount({
    serverCount: archivedCountQuery.data?.count,
    loadedCount: archivedListWarmQuery.data?.tracks?.length ?? 0,
    hasListData: archivedListWarmQuery.data != null,
    listComplete:
      archivedListWarmQuery.data != null &&
      !archivedListWarmQuery.data.pageToken,
  });

  return (
    <Container fluid>
      <Card style={{ borderRadius: 12 }}>
        <Card.Body>
          <Card.Title
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <PageTitle title="Song Library" />
              <LifecycleRulesInfoButton onClick={() => setLifecycleHelpOpen(true)} />
            </span>
            <Button
              onClick={handleReset}
              style={{
                background: 'white',
                color: '#333',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                padding: '8px 16px',
                fontWeight: '500',
              }}
            >
              <FilterOutlined /> Reset All
            </Button>
          </Card.Title>

          {lastBulkResult ? (
            <Alert
              variant={lastBulkResult.failed > 0 ? 'warning' : 'success'}
              className="mb-3"
              dismissible
              onClose={dismissLastBulkResult}
              data-testid="song-library-last-bulk-result"
            >
              Last operation ({lastBulkResult.actionLabel}): requested{' '}
              {lastBulkResult.requested}, succeeded {lastBulkResult.succeeded}, failed{' '}
              {lastBulkResult.failed}, skipped {lastBulkResult.skipped}
              {lastBulkResult.retryable > 0
                ? `, retryable ${lastBulkResult.retryable}`
                : ''}
              .{' '}
              <button
                type="button"
                onClick={() => setBulkResultsOpen(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  color: '#0d6efd',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
              >
                View results
              </button>
            </Alert>
          ) : null}

          <Nav variant="tabs" className="mb-3" data-testid="song-library-lifecycle-tabs">
            <Nav.Item>
              <Nav.Link
                active={view === 'active'}
                onClick={() => switchView('active')}
              >
                Active{formatTabCount(activeCount)}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                active={view === 'archived'}
                onClick={() => switchView('archived')}
              >
                Archived{formatTabCount(archivedCount)}
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link active={view === 'all'} onClick={() => switchView('all')}>
                All
                {formatTabCount(
                  activeCount != null &&
                    archivedCount != null &&
                    activeCount >= 0 &&
                    archivedCount >= 0
                    ? activeCount + archivedCount
                    : undefined,
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {highlightTrackId ? (
            <Alert variant="info" className="mb-3" data-testid="song-library-deeplink-banner">
              Showing linked song{' '}
              <EntityRefBadge kind="track" id={highlightTrackId} />
              {' · '}
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => clearDeepLinkTrackFilter()}
              >
                Clear filter
              </Button>
            </Alert>
          ) : null}

          {loadErrorMessage ? (
            <Alert variant="danger" data-testid="song-library-load-error">
              {loadErrorMessage}{' '}
              <Button size="sm" variant="outline-danger" onClick={() => refetchTracks()}>
                Retry
              </Button>
            </Alert>
          ) : null}

          <Form layout="vertical" className={styles.form}>
            <BootstrapRow>
              <BootstrapCol>
                <BootstrapRow className="mb-2">
                  <BootstrapCol md={4}>
                    <FormItem label="Search by song name" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`songNames-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Any song name"
                        value={filters.songNames}
                        onChange={(value) => handleFilterChange('songNames', value)}
                        options={filterOptions.songNames.map((name) => ({ label: name, value: name }))}
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem label="Search by artist" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`artists-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Any artist"
                        value={filters.artists}
                        onChange={(value) => handleFilterChange('artists', value)}
                        options={filterOptions.artists.map((artist) => ({ label: artist, value: artist }))}
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem label="Search by record label" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`recordLabels-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Any record label"
                        value={filters.recordLabels}
                        onChange={(value) => handleFilterChange('recordLabels', value)}
                        options={filterOptions.recordLabels.map((label) => ({ label, value: label }))}
                      />
                    </FormItem>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol md={4}>
                    <FormItem label="Search by ISRC" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`isrcs-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Any ISRC"
                        value={filters.isrcs}
                        onChange={(value) => handleFilterChange('isrcs', value)}
                        options={filterOptions.isrcs.map((isrc) => ({ label: isrc, value: isrc }))}
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem label="Track ID" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`trackId-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Filter by Track ID (TR-… or canonical)"
                        value={filters.trackIdFilter}
                        onChange={(value) => {
                          handleFilterChange('trackIdFilter', value);
                          // Clearing the Track ID control while a deep-link is
                          // active must also drop URL/highlight — otherwise the
                          // list query stays scoped to one Track.
                          if (!value && highlightTrackId) {
                            clearDeepLinkTrackFilter();
                          }
                        }}
                        options={songs.map((s) => ({
                          label:
                            trackRefs.get(s.trackId) ||
                            formatEntityRef('track', s.trackId),
                          value: s.trackId,
                        }))}
                        // Search accepts the human ref ("TR-52A91") AND the
                        // canonical id (any fragment) — owner requirement.
                        filterOption={(input, option) => {
                          const needle = normalizeRefSearchTerm('track', input)
                            .trim()
                            .toLowerCase();
                          if (!needle) return true;
                          return (
                            String(option?.value ?? '')
                              .toLowerCase()
                              .includes(needle) ||
                            String(option?.label ?? '')
                              .toLowerCase()
                              .includes(needle)
                          );
                        }}
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem label="Playable" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`playable-reset-${resetCounter}`}
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Playable"
                        value={filters.playableFilter}
                        onChange={(value) => handleFilterChange('playableFilter', value)}
                        options={filterOptions.playable.map((opt) => ({ label: opt, value: opt }))}
                      />
                    </FormItem>
                  </BootstrapCol>
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol md={4}>
                    <FormItem label="Genre" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`genre-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder="Any genre"
                        value={genreFilterId}
                        onChange={(value?: string) => {
                          setGenreFilterId(value || undefined);
                          // Subgenre belongs to its genre — reset on change.
                          setSubgenreFilterId(undefined);
                        }}
                        optionFilterProp="label"
                        options={activeGenres.map((g) => ({
                          label: g.name,
                          value: g.genreId,
                        }))}
                        data-testid="song-library-genre-filter"
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4}>
                    <FormItem label="Subgenre" className="customSelectWrapper" layout="vertical">
                      <Select
                        key={`subgenre-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className={`customSelect ${styles.selectField}`}
                        placeholder={genreFilterId ? 'Any subgenre' : 'Choose a genre first'}
                        disabled={!genreFilterId}
                        value={subgenreFilterId}
                        onChange={(value?: string) =>
                          setSubgenreFilterId(value || undefined)
                        }
                        optionFilterProp="label"
                        options={(
                          (genreFilterId && genreById.get(genreFilterId)?.subgenres) ||
                          []
                        )
                          .filter((s) => s.status === 'active')
                          .map((s) => ({ label: s.name, value: s.subgenreId }))}
                        data-testid="song-library-subgenre-filter"
                      />
                    </FormItem>
                  </BootstrapCol>
                  <BootstrapCol md={4} />
                </BootstrapRow>
                <BootstrapRow className="mb-2">
                  <BootstrapCol md={4}>
                    {canLifecycle ? (
                      <FormItem label="Batch Actions" className="customSelectWrapper" layout="vertical">
                        <Select<BatchActionValue>
                          className={`customSelect ${styles.selectField}`}
                          placeholder="Batch Actions"
                          // `null` (never undefined) keeps this Select
                          // controlled so antd cannot latch a stale value —
                          // see useBatchActions.ts.
                          value={batchAction}
                          onChange={handleBatchAction}
                          disabled={
                            selectedRowKeys.length === 0 ||
                            batchOptions.length === 0
                          }
                          options={batchOptions}
                        />
                      </FormItem>
                    ) : null}
                  </BootstrapCol>
                  <BootstrapCol md={4} />
                  <BootstrapCol md={4} className="d-flex align-items-center mt-2 mt-md-0">
                    <Button
                      className="w-100"
                      style={{
                        background: 'white',
                        color: '#e21e26',
                        border: '1px solid #e21e26',
                        borderRadius: '6px',
                        padding: '8px 16px',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        justifyContent: 'center',
                        height: '50px',
                        marginTop: '8px',
                      }}
                      onClick={() => navigate('/uploader')}
                    >
                      <PlusOutlined style={{ fontSize: 16 }} />
                      Upload New Song
                    </Button>
                  </BootstrapCol>
                </BootstrapRow>
              </BootstrapCol>
            </BootstrapRow>
          </Form>

          <Table
            className={`legacy-row-height-table ${styles.table}`}
            rowSelection={rowSelection}
            columns={columns}
            dataSource={filteredSongs}
            pagination={{
              showQuickJumper: true,
              pageSize: TABLE_PAGE_SIZE,
              current: tablePage,
              onChange: (page) => setTablePage(page),
            }}
            rowKey="trackId"
            loading={tracksLoading || tracksFetching}
            rowClassName={(row) =>
              highlightTrackId && row.trackId === highlightTrackId
                ? styles.highlightedRow
                : ''
            }
          />
        </Card.Body>
      </Card>

      <input
        ref={replaceFileInputRef}
        type="file"
        accept="audio/mpeg,.mp3"
        style={{ display: 'none' }}
        onChange={onReplaceFilePicked}
        data-testid="song-library-replace-file-input"
      />

      <ReplaceSongAudioConfirmModal
        show={replaceTarget != null}
        target={replaceTarget}
        busy={replaceBusy}
        errorMessage={replaceError}
        errorOpenPath={replaceErrorOpenPath}
        onCancel={() => {
          if (replaceBusy) return;
          setReplaceTarget(null);
          setReplaceError('');
          setReplaceErrorOpenPath(null);
        }}
        onConfirm={(reason) => void submitReplaceSongAudio(reason)}
      />

      <Modal
        open={genreEditTarget != null}
        onCancel={closeGenreEdit}
        footer={null}
        width={480}
        destroyOnClose
      >
        <div style={{ padding: 24 }} data-testid="song-genre-edit-modal">
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 16 }}>
            Edit Genre — {genreEditTarget?.title}
          </div>
          <FormItem label="Genre (optional)" layout="vertical">
            <Select
              showSearch
              allowClear
              className={`customSelect ${styles.selectField}`}
              placeholder="No genre"
              value={genreEditGenreId ?? undefined}
              onChange={(value?: string) => {
                setGenreEditGenreId(value || null);
                // Clearing or changing Genre clears the Subgenre selection —
                // a subgenre must belong to the selected genre.
                setGenreEditSubgenreId(null);
              }}
              optionFilterProp="label"
              options={genreEditGenreOptions}
              data-testid="song-genre-edit-genre-select"
            />
          </FormItem>
          <FormItem label="Subgenre (optional)" layout="vertical">
            <Select
              showSearch
              allowClear
              className={`customSelect ${styles.selectField}`}
              placeholder={genreEditGenreId ? 'No subgenre' : 'Choose a genre first'}
              disabled={!genreEditGenreId}
              value={genreEditSubgenreId ?? undefined}
              onChange={(value?: string) => setGenreEditSubgenreId(value || null)}
              optionFilterProp="label"
              options={genreEditSubgenreOptions}
              data-testid="song-genre-edit-subgenre-select"
            />
          </FormItem>
          <div
            style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}
          >
            <AntdButton disabled={genreEditSaving} onClick={closeGenreEdit}>
              Cancel
            </AntdButton>
            <AntdButton
              type="primary"
              loading={genreEditSaving}
              onClick={() => void submitGenreEdit()}
              data-testid="song-genre-edit-save"
            >
              Save
            </AntdButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={archiveModalOpen}
        onCancel={() => {
          if (archiveLoading) return;
          setArchiveModalOpen(false);
          setTrackToArchive(null);
          setArchiveImpact(null);
          setArchiveImpactError(null);
        }}
        footer={null}
        width={520}
        className="kk-modal-center-in-content kk-modal-align-page-card"
      >
        <div style={{ padding: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 12 }}>
            Archive &quot;{trackToArchive?.title}&quot;?
          </div>
          <div style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
            The song moves to Archived Song Library and becomes not playable.
            Song Processing jobs stay protected until the song is permanently deleted.
            It will be removed from all Channel Programming schedules and will not
            be reinserted on Restore.
          </div>
          {archiveImpactLoading ? (
            <div style={{ color: '#666', marginBottom: 16 }}>
              Loading channel programming impact…
            </div>
          ) : null}
          {archiveImpactError ? (
            <Alert variant="danger" className="mb-3">
              {archiveImpactError}
            </Alert>
          ) : null}
          {archiveImpact?.operatorMessage ||
          (archiveImpact?.channels && archiveImpact.channels.length > 0) ? (
            <Alert variant="warning" className="mb-3" style={{ textAlign: 'left' }}>
              {archiveImpact.operatorMessage ||
                'This Song is used in Channel Programming. If you continue, it will be removed from these channels.'}
              <ChannelImpactChannelList
                channels={archiveImpact.channels || []}
              />
            </Alert>
          ) : null}
          {archiveImpact?.blocked ? (
            <Alert variant="danger" className="mb-3" style={{ textAlign: 'left' }}>
              <div style={{ marginBottom: 8 }}>
                {(archiveImpact.blockers || [])
                  .map((b) => b.message)
                  .join(' ')}
              </div>
              {(archiveImpact.blockers || []).some(
                (b) => b.channelName || b.rotationName || b.scheduleSide,
              ) ? (
                <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                  {(archiveImpact.blockers || []).map((b, idx) => (
                    <li
                      key={`${b.code || 'block'}-${b.rotationId || b.channelId || idx}`}
                      style={{ marginBottom: 8 }}
                    >
                      <SchedulePlacementStack
                        scheduleSide={b.scheduleSide}
                        channel={b.channelName || b.channelId}
                        rotation={b.rotationName || b.rotationId}
                        compact
                      />
                    </li>
                  ))}
                </ul>
              ) : null}
            </Alert>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16 }}>
            <AntdButton
              disabled={!!archiveLoading}
              onClick={() => {
                setArchiveModalOpen(false);
                setTrackToArchive(null);
                setArchiveImpact(null);
                setArchiveImpactError(null);
              }}
            >
              Cancel
            </AntdButton>
            <AntdButton
              type="primary"
              danger
              onClick={() => void handleConfirmArchive()}
              loading={
                archiveImpactLoading ||
                (trackToArchive
                  ? archiveLoading === trackToArchive.trackId
                  : false)
              }
              disabled={
                archiveImpactLoading ||
                !!archiveImpactError ||
                !!archiveImpact?.blocked
              }
            >
              Archive
            </AntdButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={restoreModalOpen}
        onCancel={() => {
          setRestoreModalOpen(false);
          setTrackToRestore(null);
        }}
        footer={null}
        width={430}
        className="kk-modal-center-in-content"
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            Restore &quot;{trackToRestore?.title}&quot;?
          </div>
          <div style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
            The song returns to Active Song Library. Playback resumes when an
            Approved artifact pointer is still present.
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
            <AntdButton
              onClick={() => {
                setRestoreModalOpen(false);
                setTrackToRestore(null);
              }}
            >
              Cancel
            </AntdButton>
            <AntdButton
              type="primary"
              className={styles.brandPrimary}
              onClick={handleConfirmRestore}
              loading={trackToRestore ? restoreLoading === trackToRestore.trackId : false}
            >
              Restore
            </AntdButton>
          </div>
        </div>
      </Modal>

      <Modal
        open={deleteModalOpen}
        onCancel={() => {
          // Allow dismiss during durable BulkOp run — server continues; pointer remains.
          if (deleteLoading && deletePhase === 'running' && pdBulkProgress) {
            setDeleteModalOpen(false);
            return;
          }
          if (deleteLoading) return;
          // Cancelled confirmation — fully reset so the next batch can start
          // immediately (owner requirement 2026-07-21).
          closeDeleteModal();
          resetBatchUi();
        }}
        footer={null}
        width={560}
        destroyOnClose
        className="kk-modal-center-in-content kk-modal-align-page-card"
      >
        <div style={{ padding: 24 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 12 }}>
            {deletePhase === 'running' && pdBulkProgress
              ? `Permanently deleting ${pdBulkProgress.total} songs…`
              : <>
                  Permanently delete{' '}
                  {bulkDeleteIds.length > 1
                    ? `${bulkDeleteIds.length} songs`
                    : `"${deleteTarget?.title || ''}"`}
                  ?
                </>}
          </div>
          {deletePhase === 'running' && pdBulkProgress ? (
            <div style={{ color: '#666', marginBottom: 16 }}>
              Completed {pdBulkProgress.completed} of {pdBulkProgress.total}
              <div style={{ marginTop: 8, fontSize: 13 }}>
                You can close this dialog or leave the page — deletion continues
                on the server. Return to Song Library to resume progress.
              </div>
            </div>
          ) : null}
          {deletePhase === 'preflight' ? (
            <div
              style={{
                color: '#444',
                marginBottom: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
              role="status"
              aria-live="polite"
              data-testid="permanent-delete-preflight-loading"
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                <LoadingOutlined style={{ color: '#e21e26', fontSize: 18 }} spin />
                Preparing deletion summary…
              </div>
              <div style={{ color: '#666', fontSize: 14 }}>
                {bulkDeleteIds.length > 1
                  ? `Checking ${bulkDeleteIds.length} song lineages. This may take a moment.`
                  : 'Checking song lineage. This may take a moment.'}
              </div>
              <div style={{ color: '#888', fontSize: 12 }}>
                Permanent Delete stays disabled until every lineage check finishes.
                No deletion has started.
              </div>
            </div>
          ) : null}
          {deleteError ? (
            <Alert variant="danger" className="mb-3">
              {deleteError}
            </Alert>
          ) : null}
          {deletePreflight ? (
            <div style={{ color: '#444', fontSize: 14, marginBottom: 16, textAlign: 'left' }}>
              {deleteTarget && bulkDeleteIds.length <= 1 ? (
                <div style={{ marginBottom: 8 }}>
                  <div>
                    <strong>Title:</strong> {deletePreflight.title}
                  </div>
                  <div>
                    <strong>Track ID:</strong>{' '}
                    <EntityRefBadge kind="track" id={deletePreflight.trackId} />
                  </div>
                </div>
              ) : null}
              <div style={{ marginBottom: 8 }}>
                <strong>Will remove</strong>
                <ul style={{ marginBottom: 8 }}>
                  <li>Song Processing jobs: {deletePreflight.counts.processingJobs}</li>
                  <li>
                    Approved Artifact Versions:{' '}
                    {deletePreflight.counts.approvedArtifactVersions}
                  </li>
                  <li>Playback objects: {deletePreflight.counts.playbackObjects}</li>
                  <li>
                    Programming/channel refs:{' '}
                    {deletePreflight.counts.programmingReferences}
                    {deletePreflight.counts.channelCount
                      ? ` across ${deletePreflight.counts.channelCount} channel(s)`
                      : ''}
                  </li>
                </ul>
                {deletePreflight.channelImpact?.operatorMessage ||
                (deletePreflight.channelImpact?.channels &&
                  deletePreflight.channelImpact.channels.length > 0) ? (
                  <Alert variant="warning" className="mb-3">
                    {deletePreflight.channelImpact.operatorMessage ||
                      'Selected songs are used in Channel Programming and will be removed from these channels.'}
                    <ChannelImpactChannelList
                      channels={deletePreflight.channelImpact.channels || []}
                    />
                  </Alert>
                ) : null}
                {deletePreflight.stalePointers &&
                deletePreflight.stalePointers.length > 0 ? (
                  <Alert variant="warning" className="mb-3">
                    <strong>Stale pointers detected</strong>
                    <ul style={{ marginBottom: 0, marginTop: 8 }}>
                      {deletePreflight.stalePointers.slice(0, 12).map((w) => (
                        <li key={`${w.code}-${w.pointer || w.message}`}>
                          {w.message}
                        </li>
                      ))}
                    </ul>
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                      Live Jobs/AAVs may show 0 while the Track still holds
                      playback or job pointers. Permanent delete will clear those
                      pointers on the Track tombstone.
                    </div>
                  </Alert>
                ) : null}
                <strong>Will retain forever</strong>
                <ul>
                  {(deletePreflight.willRetain || []).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </div>
              {!deletePreflight.eligible ? (
                <Alert variant="warning">
                  <div style={{ marginBottom: 8 }}>
                    Deletion blocked:{' '}
                    {deletePreflight.blockers.map((b) => b.message).join(' ')}
                  </div>
                  {deletePreflight.blockers.some(
                    (b) => b.channelName || b.rotationName || b.scheduleSide,
                  ) ? (
                    <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
                      {deletePreflight.blockers.map((b, idx) => (
                        <li
                          key={`${b.code || 'block'}-${b.rotationId || b.channelId || idx}`}
                          style={{ marginBottom: 8 }}
                        >
                          <SchedulePlacementStack
                            scheduleSide={b.scheduleSide}
                            channel={b.channelName || b.channelId}
                            rotation={b.rotationName || b.rotationId}
                            compact
                          />
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </Alert>
              ) : null}
            </div>
          ) : null}
          {deletePhase === 'confirm' || deletePhase === 'running' || deletePhase === 'error' ? (
            <>
              <FormItem label={`Type ${DELETE_CONFIRM_TOKEN} to confirm`} layout="vertical">
                <input
                  className="form-control"
                  value={deleteConfirmTyped}
                  onChange={(e) => setDeleteConfirmTyped(e.target.value)}
                  disabled={deleteLoading}
                  autoComplete="off"
                />
              </FormItem>
              <FormItem label="Deletion reason" layout="vertical">
                <input
                  className="form-control"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  disabled={deleteLoading}
                  placeholder="Why is this song being permanently deleted?"
                />
              </FormItem>
            </>
          ) : null}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 16 }}>
            <AntdButton
              disabled={deleteLoading && deletePhase !== 'running'}
              onClick={() => {
                if (deletePhase === 'running' && pdBulkProgress) {
                  setDeleteModalOpen(false);
                  return;
                }
                closeDeleteModal();
                resetBatchUi();
              }}
            >
              {deletePhase === 'running' && pdBulkProgress ? 'Close' : 'Cancel'}
            </AntdButton>
            <AntdButton
              type="primary"
              danger
              loading={deleteLoading || deletePhase === 'preflight'}
              disabled={
                deletePhase === 'running' ||
                !deletePreflight?.eligible ||
                deleteConfirmTyped !== DELETE_CONFIRM_TOKEN ||
                !deleteReason.trim()
              }
              onClick={() => void handleConfirmPermanentDelete()}
            >
              {deletePhase === 'running'
                ? pdBulkProgress
                  ? `Completed ${pdBulkProgress.completed} of ${pdBulkProgress.total}`
                  : 'Deleting…'
                : 'Permanently Delete'}
            </AntdButton>
          </div>
        </div>
      </Modal>

      <BulkOperationResultsModal
        open={bulkResultsOpen}
        summary={lastBulkResult}
        onClose={() => setBulkResultsOpen(false)}
        onRetryRetryable={(ids) => void handleRetryBulkRetryable(ids)}
        retryBusy={bulkRetryBusy}
      />

      <Modal
        open={batchModalOpen}
        onCancel={() => {
          if (batchConfirmBusy) return;
          clearBatchAction();
        }}
        footer={null}
        width={430}
        closable={!batchConfirmBusy}
        maskClosable={!batchConfirmBusy}
        className="kk-modal-center-in-content"
      >
        <div style={{ textAlign: 'center', padding: 32 }}>
          <div style={{ fontWeight: 600, fontSize: 17, marginBottom: 18 }}>
            {batchAction === 'restore'
              ? `Restore ${batchTargetIds.length} selected ${
                  selectionLifecycle === 'mixed' ? 'Archived ' : ''
                }song(s)?`
              : `Archive ${batchTargetIds.length} selected ${
                  selectionLifecycle === 'mixed' ? 'Active ' : ''
                }song(s)?`}
          </div>
          <div style={{ color: '#666', fontSize: 14, marginBottom: 24 }}>
            {batchAction === 'restore'
              ? 'Each song returns to Active. Track ID, jobs, and AAVs are preserved.'
              : 'Each song moves to Archived and becomes not playable.'}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
            <AntdButton onClick={clearBatchAction} disabled={batchConfirmBusy}>
              Cancel
            </AntdButton>
            <AntdButton
              type="primary"
              danger={batchAction === 'archive'}
              className={
                batchAction === 'restore' ? styles.brandPrimary : undefined
              }
              onClick={() => void handleConfirmBatch()}
              loading={batchConfirmBusy}
            >
              {batchAction === 'restore' ? 'Restore' : 'Archive'}
            </AntdButton>
          </div>
        </div>
      </Modal>

      <LifecycleRulesDrawer
        content={SONG_LIFECYCLE_RULES}
        open={lifecycleHelpOpen}
        onClose={() => setLifecycleHelpOpen(false)}
      />
    </Container>
  );
};

export default withAuthenticationRequired(Songs);
